import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { combineLatest, map, take, tap, BehaviorSubject, startWith } from 'rxjs';
import { RestrictionDetailsComponent } from 'src/app/components/restriction-details/restriction-details.component';
import { Restriction } from 'src/app/models/restriction';
import { RestrictionsService } from 'src/app/services/restrictions.service';
import { RestrictionDialogComponent } from 'src/app/components/restriction-dialog/restriction-dialog.component';
import { ButtonModule } from 'primeng/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ListboxModule } from 'primeng/listbox';
import { CardModule } from 'primeng/card';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HeaderComponent } from 'src/app/components/header/header.component';
import { UserService } from 'src/app/services/user.service';
import { MOBILE_BREAKPOINT } from 'src/app/shared/breakpoints';

@Component({
  selector: 'app-vehicle',
  standalone: true,
  imports: [
    TabsModule,
    RestrictionDetailsComponent,
    AsyncPipe,
    RestrictionDialogComponent,
    ButtonModule,
    FormsModule,
    ListboxModule,
    CardModule,
    FormsModule, 
    ReactiveFormsModule,
    HeaderComponent
  ],
  templateUrl: './vehicle.component.html',
  styleUrl: './vehicle.component.scss',
})
export class VehicleComponent implements OnInit {
  public restrictions$ = this.restrictionsService.restrictions$;

  private currentVehicleName$ = this.route.paramMap.pipe(
    map(params => params.get('name') ?? undefined)
  );

  // Set synchronously in onEditSelected(), alongside showEditDialog, so the dialog never opens
  // with a stale/empty editRestriction (which previously raced with the restrictions$ | async pipe).
  editingRestriction: Restriction | null = null;

  public vehicleName$ = combineLatest({
    name: this.currentVehicleName$,
    restrictions: this.restrictions$
  }).pipe(
    map(({ name, restrictions }) => {
      if (!name) return '';
      const vehicles = (restrictions ?? []).flatMap(r => r.vehicles ?? []);
      const match = vehicles.find(v => v.name === name);
      return match?.name ?? '';
    })
  );

  selectedIndex = 0;
  selectedRestriction?: Restriction;
  showEditDialog = false;
  isAdmin$ = this.userService.isAdmin$;

  isMobile$ = this.breakpointObserver.observe(MOBILE_BREAKPOINT).pipe(map(r => r.matches));

  constructor(
    private route: ActivatedRoute,
    private restrictionsService: RestrictionsService,
    private destroyRef: DestroyRef,
    private router: Router,
    private userService: UserService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    this.currentVehicleName$.pipe(
      tap(name => this.restrictionsService.setCurrentVehicleName(name)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  // 2-letter initials helper
  abbr(text?: string | null): string {
    if (!text) return '';
    const letters = (text.match(/[A-Za-z]+/g)?.join('') || '').toUpperCase();
    return (letters.slice(0, 2) || text.slice(0, 2)).toUpperCase();
  }

  makeAbbr(text: string | null | undefined): string {
    if (!text) return '';
    const letters = (text.match(/[A-Za-z]+/g)?.join('') || '').toUpperCase();
    return letters.slice(0, 2) || text.slice(0, 2).toUpperCase();
  }

  onEditSelected() {
    this.restrictions$.pipe(take(1)).subscribe(restrictions => {
      const restriction = restrictions[this.selectedIndex];
      this.editingRestriction = restriction ? { ...restriction } : null;
      this.showEditDialog = true;
    });
  }

  onDialogSave(updated: Restriction) {
    this.showEditDialog = false;
    // refresh data here
  }

  public searchTermControl = new FormControl<string>('')
  public searchTerm = ""

  public sortOrderSubject = new BehaviorSubject<boolean>(true)

  public filteredRestrictions$ = combineLatest({
    restrictions: this.restrictionsService.restrictions$,
    searchTerm: this.searchTermControl.valueChanges.pipe(startWith('')),
  }).pipe(
    map(source => {

      this.searchTerm = source.searchTerm!

      let restrictions = source.restrictions ?? []

      if(restrictions.length && source.searchTerm) {
        const searchLower = source.searchTerm.toLowerCase();
        restrictions = restrictions.filter(restriction => {
          const normalize = (str: string) => str.toLowerCase().replace(/\s+/g, ''); // Remove spaces
          const searchNormalized = normalize(searchLower);
        
          return normalize(restriction.displayName).includes(searchNormalized) ||
                 normalize(restriction.restrictionName).includes(searchNormalized);
        });
      }

      return restrictions
    })
  )

  showDetailsMobile = false;
  selectedRestrictionMobile = null;

  onSelectMobile(restriction: any, list: any[]) {
    this.selectedRestrictionMobile = restriction;
    this.selectedIndex = list.indexOf(restriction);
    this.showDetailsMobile = true;
  }

  onCloseDetails() {
    this.showDetailsMobile = false;
  }

  onBack() {
    if (this.showDetailsMobile) {
      // Back from details to list on mobile
      this.showDetailsMobile = false;
    } else {
      // Back to the vehicles overview route
      this.router.navigate(['/vehicles']);
    }
  }
}
